import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bot, Globe, MessageSquare, LayoutDashboard, ArrowRight, CheckCircle2, Zap, Search, Rocket, Handshake } from 'lucide-react';
import CorporateNavbar from '@/components/CorporateNavbar';
import CorporateFooter from '@/components/CorporateFooter';
import LightPageHero from '@/components/LightPageHero';

const services = [
  {
    icon: Bot,
    title: 'AI-Agenter',
    description: 'En agent som aldrig sover, aldrig klagar och aldrig glömmer att följa upp. Hanterar ärenden, svarar kunder och håller ordning — dygnet runt.',
    bullets: ['Lead-kvalificering & CRM-uppdatering', 'Kundtjänst & ärendehantering', 'Intern kunskapsbas & FAQ-bot'],
    gradient: 'from-indigo-500 to-violet-600',
    glow: 'rgba(99,102,241,0.3)',
  },
  {
    icon: Globe,
    title: 'AI-Drivna Webbplatser',
    description: 'Moderna webbplatser med AI-integration — personalisering, smarta formulär och realtidsanalys.',
    bullets: ['Konverteringsoptimerade landningssidor', 'Integrerade chatbotar', 'SEO & prestandaoptimering'],
    gradient: 'from-violet-500 to-purple-600',
    glow: 'rgba(139,92,246,0.3)',
  },
  {
    icon: MessageSquare,
    title: 'AI-Chatbotar',
    description: 'Din mest tålmodiga medarbetare. Svarar direkt, på rätt sätt, oavsett tid på dygnet. Integreras med din sajt, Facebook eller WhatsApp.',
    bullets: ['GPT-4 & Claude-integration', 'Flerspråkigt stöd', 'Anpassat till er tonalitet & brand'],
    gradient: 'from-purple-500 to-pink-500',
    glow: 'rgba(168,85,247,0.3)',
  },
  {
    icon: LayoutDashboard,
    title: 'Micro SaaS-Applikationer',
    description: 'Egenutvecklade SaaS-produkter som löser specifika affärsproblem — byggda snabbt, skalbara från dag ett.',
    bullets: ['Från idé till MVP på veckor', 'Stripe-betalningar & prenumerationer', 'Admin-panel & analysdashboard'],
    gradient: 'from-amber-400 to-orange-500',
    glow: 'rgba(251,146,60,0.3)',
  },
];

const process = [
  { icon: Search,    step: '01', title: 'Discovery',    desc: 'Vi kartlägger era processer och identifierar var AI ger störst effekt.' },
  { icon: Rocket,    step: '02', title: 'Prototyp',     desc: 'Snabb prototyp på 1–2 veckor så ni ser resultaten innan full leverans.' },
  { icon: Zap,       step: '03', title: 'Leverans',     desc: 'Driftsätter och integrerar lösningen mot era befintliga system.' },
  { icon: Handshake, step: '04', title: 'Support',      desc: 'Löpande uppföljning, optimering och support efter lansering.' },
];

const fadeUp = {
  hidden:  { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  }),
};

const AIConsulting = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Helmet>
        <title>AI-tjänster & Automationer — Aifinity AB</title>
        <meta name="description" content="Vi bygger AI-agenter, chatbotar och automationer som sparar tid varje dag. Konkreta verktyg för svenska B2B-företag — inte PowerPoints om framtiden." />
        <meta property="og:url" content="https://aifinity.se/ai-consulting" />
        <link rel="canonical" href="https://aifinity.se/ai-consulting" />
      </Helmet>
      <CorporateNavbar />

      <LightPageHero
        badge="Vad vi bygger"
        heading={<>Vi löser det som<br /><span style={{ background: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #60a5fa 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>tar för lång tid</span></>}
        subtext="Konkreta verktyg och system som faktiskt används — inte PowerPoints om framtiden."
        ctaLabel="Beskriv ditt behov"
        ctaHref="/contact"
      />

      {/* ── Services ── */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-16"
          >
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">Vad vi bygger</span>
            <h2 className="text-3xl sm:text-[40px] font-extrabold text-text-primary tracking-[-0.02em] leading-tight">
              Fyra kärnområden
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((s, i) => (
              <motion.div
                key={s.title}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="group bg-white rounded-2xl border border-border-light p-8 transition-all duration-300"
                style={{ boxShadow: '0 2px 8px rgba(99,102,241,0.08), 0 0 0 1px rgba(99,102,241,0.06)' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 8px 24px rgba(99,102,241,0.15), 0 20px 60px ${s.glow}`; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(99,102,241,0.08), 0 0 0 1px rgba(99,102,241,0.06)'; }}
              >
                <div className={`inline-flex w-11 h-11 rounded-xl bg-gradient-to-br ${s.gradient} items-center justify-center mb-5 shadow-md`}
                  style={{ boxShadow: `0 4px 14px ${s.glow}` }}>
                  <s.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-text-primary mb-3">{s.title}</h3>
                <p className="text-[15px] text-text-secondary leading-relaxed mb-5">{s.description}</p>
                <ul className="space-y-2">
                  {s.bullets.map(b => (
                    <li key={b} className="flex items-start gap-2 text-sm text-text-secondary">
                      <CheckCircle2 className="w-4 h-4 text-brand-indigo mt-0.5 shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Process ── */}
      <section className="py-24 bg-surface-subtle">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-16"
          >
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">Hur vi arbetar</span>
            <h2 className="text-3xl sm:text-[40px] font-extrabold text-text-primary tracking-[-0.02em] leading-tight">
              Från idé till drift
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((p, i) => (
              <div
                key={p.step}
                className="bg-white rounded-2xl p-6 border border-border-light"
                style={{ boxShadow: '0 2px 8px rgba(99,102,241,0.08), 0 0 0 1px rgba(99,102,241,0.06)' }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-md">
                    <p.icon className="w-4.5 h-4.5 text-white" />
                  </div>
                  <span className="text-xs font-bold tracking-widest text-brand-indigo">{p.step}</span>
                </div>
                <h3 className="text-[17px] font-bold text-text-primary mb-2">{p.title}</h3>
                <p className="text-sm text-text-secondary leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA strip — links to /contact instead of duplicate form ── */}
      <section className="py-20 bg-white border-t border-border-light">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="text-3xl font-extrabold text-text-primary tracking-tight mb-4">
              Redo att komma igång?
            </h2>
            <p className="text-text-secondary mb-8 max-w-md mx-auto">
              Berätta vad som tar tid idag — vi återkommer inom 24 timmar med ett konkret upplägg.
            </p>
            <Link to="/contact">
              <button className="group btn-glow relative overflow-hidden bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-600 text-white font-semibold px-8 py-3.5 rounded-xl text-sm inline-flex items-center gap-2">
                Skicka förfrågan
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      <CorporateFooter />
    </div>
  );
};

export default AIConsulting;
